import {FC, MouseEvent, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {useAnalytics} from "@common-core/react-analytics";
import {Popover} from "@interstate/components/Popover";
import {useNavigation} from "../../../../navigation";
import {EntityCard} from "../../../card";
import {TabColumn} from "../../../../tab-content";
import {TabRow} from "../../../../tab-content";
import {useOperationalEntity} from "../../../context";
import {OperationalDetailsHeaderV2} from "./OperationalDetailsHeaderV2";
import {Sources} from "../../../overview";
import {EntityStatusBadge} from "../../../badges";
import {CheckCircleIcon, XMarkIcon} from "@interstate/components/Icons";
import {Tooltip} from "@interstate/components/Tooltip";
import {Button} from "@interstate/components/Button";
import {TextInput} from "@interstate/components/TextInput";
import {Spinner} from "@interstate/components/Spinner";
import {OperationalAddress} from "../OperationalAddress";
import {OperationalOems} from "../OperationalOems";
import {SyntheticOperationalOems} from "./SyntheticOperationalOems";
import {OperationalTelecomNumbers} from "../OperationalTelecomNumbers";
import {EnterpriseEntitlements} from "../EnterpriseEntitlements";
import {SyntheticBadge} from "../SyntheticBadge";
import {useTenancy} from "../../hooks/useTenancy";
import "../OperationalBusinessInformation.scss";
import "../OperationalAddress.scss";
import "../OperationalTelecomNumbers.scss";
import {AddressTableBody} from "../../../overview/AddressTableBody";
import {useGraphqlLazyQuery, useGraphqlMutation} from "../../../../amplify/hooks";
import {
  lookupOperationalEntity as lookupOperationalEntityQuery,
  updateOperationalEntityDbaName as updateDbaNameMutation,
  updateOperationalEntityAddress as updateAddressMutation,
  updateOperationalEntityTelecomNumbers as updateTelecomMutation
} from "../../../../amplify/graphql";
import {
  UpdateOperationalEntityDbaNameData,
  UpdateOperationalEntityDbaNameVariables,
  UpdateOperationalEntityAddressData,
  UpdateOperationalEntityAddressVariables,
  UpdateOperationalEntityTelecomNumbersData,
  UpdateOperationalEntityTelecomNumbersVariables,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  assertOperationalEntityPresent
} from "../../../backend";
import {
  SimpleTable,
  TableBody,
  TableRow,
  TableCell
} from "@interstate/components/SimpleTable";
import {
  EntityStatus,
  OrganizationInformation
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {
  ActionButtons,
  CopyButton,
  EditButton,
  PermissionBasedEditButton
} from "../../../../action-buttons";
import {EDIT_PARENT_ID} from "../../../../access";
import {useFlags} from "launchdarkly-react-client-sdk";
import {features} from "../../../../features";
import {EditParentModalV2} from "../EditParentModalV2";
import {OnCompleteCallback} from "../../../../utils";

export const OperationalDetailsColumnV2: FC = () => {
  const {
    entity: {
      id,
      tenantId,
      parent,
      organizationInformation = {} as OrganizationInformation,
      oems,
      statuses = [],
      sources,
      isComplex
    },
    setEntity
  } = useOperationalEntity();

  const [editingParent, setEditingParent] = useState(false);
  const [editingDbaName, setEditingDbaName] = useState(false);
  const [savingDbaName, setSavingDbaName] = useState(false);
  const [dbaValue, setDbaValue] = useState("");
  const [editingAddress, setEditingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [editingTelecom, setEditingTelecom] = useState(false);
  const [savingTelecom, setSavingTelecom] = useState(false);
  const [telecomValue, setTelecomValue] = useState("");
  const [addressForm, setAddressForm] = useState({
    streetAddressLine1: "",
    streetAddressLine2: "",
    city: "",
    state: "",
    zip: "",
    country: ""
  });

  const finishParentEditing: OnCompleteCallback = () => {
    setEditingParent(false);
  };

  const history = useHistory();
  const location = useLocation();
  const {navigateToEntityFromEvent} = useNavigation();
  const {productEvent} = useAnalytics();
  const flags = useFlags();
  const showTenancyTab = flags[features.dev.tenancy.tenancyTab];
  const activateTenancyTab = (event: MouseEvent) => {
    event.preventDefault();
    history.push({...location, hash: "#tenancy"});
  };

  const {tenant} = useTenancy(tenantId ?? undefined);
  const isSynthetic = tenant?.synthetic === true;
  // Address, telecom, and OEMs use the CACM modal editors when the entity has a
  // CACM source; otherwise (and always for business information) the custom
  // inline editors are used.
  const hasCacmSource = (sources ?? []).some(
    source => source.sourceType === "CACM"
  );
  const {address, telecomNumbers, dbaName, legalName} = organizationInformation;

  const [lookupEntity] = useGraphqlLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >({amplifyQuery: lookupOperationalEntityQuery});

  const [updateDbaName] = useGraphqlMutation<
    UpdateOperationalEntityDbaNameData,
    UpdateOperationalEntityDbaNameVariables
  >({amplifyMutation: updateDbaNameMutation});

  const [updateAddress] = useGraphqlMutation<
    UpdateOperationalEntityAddressData,
    UpdateOperationalEntityAddressVariables
  >({amplifyMutation: updateAddressMutation});

  const [updateTelecom] = useGraphqlMutation<
    UpdateOperationalEntityTelecomNumbersData,
    UpdateOperationalEntityTelecomNumbersVariables
  >({amplifyMutation: updateTelecomMutation});

  const refreshEntity = async () => {
    const result = await lookupEntity({variables: {id}});
    const updated = assertOperationalEntityPresent(result);
    setEntity(updated);
  };

  const startEditingDbaName = () => {
    setDbaValue(dbaName ?? "");
    setEditingDbaName(true);
  };

  const handleDbaNameSave = async () => {
    if (dbaValue === (dbaName ?? "")) {
      setEditingDbaName(false);
      return;
    }
    setSavingDbaName(true);
    try {
      await updateDbaName({
        variables: {dbaNameInput: {entityId: id, dbaName: dbaValue}}
      });
      await refreshEntity();
      setEditingDbaName(false);
    } finally {
      setSavingDbaName(false);
    }
  };

  const startEditingAddress = () => {
    setAddressForm({
      streetAddressLine1: address?.streetAddressLine1 ?? "",
      streetAddressLine2: address?.streetAddressLine2 ?? "",
      city: address?.city ?? "",
      state: address?.state ?? "",
      zip: address?.zip ?? "",
      country: address?.country ?? ""
    });
    setEditingAddress(true);
  };

  const handleAddressSave = async () => {
    setSavingAddress(true);
    try {
      await updateAddress({
        variables: {
          addressInput: {
            entityId: id,
            address: {
              streetAddressLine1: addressForm.streetAddressLine1 || null,
              streetAddressLine2: addressForm.streetAddressLine2 || null,
              city: addressForm.city || null,
              state: addressForm.state || null,
              zip: addressForm.zip || null,
              country: addressForm.country || null
            }
          }
        }
      });
      await refreshEntity();
      setEditingAddress(false);
    } finally {
      setSavingAddress(false);
    }
  };

  const startEditingTelecom = () => {
    setTelecomValue((telecomNumbers ?? []).join(", "));
    setEditingTelecom(true);
  };

  const handleTelecomSave = async () => {
    const currentDisplay = (telecomNumbers ?? []).join(", ");
    if (telecomValue === currentDisplay) {
      setEditingTelecom(false);
      return;
    }
    setSavingTelecom(true);
    try {
      const numbers = telecomValue.split(",").map(n => n.trim()).filter(Boolean);
      await updateTelecom({
        variables: {telecomNumbersInput: {entityId: id, telecomNumbers: numbers}}
      });
      await refreshEntity();
      setEditingTelecom(false);
    } finally {
      setSavingTelecom(false);
    }
  };

  const updateAddressField = (field: string, value: string) =>
    setAddressForm(prev => ({...prev, [field]: value}));

  return (
    <TabColumn
      name={"details-column"}
      header={<OperationalDetailsHeaderV2 />}
    >
      <TabRow name={"dealer-status-bar"}>
        <div className={"coat-badge-bar entity-status-bar"} data-testid={`entity-status-bar-${id}`} id={`entity-status-bar-${id}`}>
          <SyntheticBadge isSynthetic={isSynthetic} />
          {(isComplex ? [...statuses, EntityStatus.COMPLEX] : statuses)
            .filter(s => s !== EntityStatus.PROTECTED)
            .map(s => (
              <EntityStatusBadge key={s} id={id} status={s} compact={false} />
            ))}
        </div>
      </TabRow>
      <TabRow name={"business-information"} header={<h4>Business Information</h4>}>
        {editingParent && (
          <EditParentModalV2
            id={id}
            currentParent={parent?.id}
            onComplete={finishParentEditing}
          />
        )}
        <SimpleTable
          id={`v2-business-information-table-${id}`}
          data-testid={`v2-business-information-table-${id}`}
          dataDensity={"small"}
          background={"white"}
          bordered={false}
          hover={false}
          striped={false}
        >
          <TableBody>
            <TableRow>
              <TableCell className={"information-field"}>Legal Name</TableCell>
              <TableCell className={"information-value"}>{legalName}</TableCell>
              <TableCell className={"information-actions"} />
            </TableRow>
            <TableRow>
              <TableCell className={"information-field"}>DBA Name</TableCell>
              <TableCell className={"information-value"} data-testid="inline-editor-dba-name">
                {editingDbaName ? (
                  <fieldset disabled={savingDbaName} style={{border: "none", padding: 0, margin: 0, opacity: savingDbaName ? 0.5 : 1}}>
                    <TextInput
                      id={"inline-input-dba-name"}
                      data-testid={"inline-input-dba-name"}
                      name={"inline-input-dba-name"}
                      label={"DBA Name"}
                      displayLabel={false}
                      size={"small"}
                      value={dbaValue}
                      onChange={(event: any) => setDbaValue(event.target.value)}
                      autoFocus
                      onKeyDown={(event: any) => {
                        if (event.key === "Enter") handleDbaNameSave();
                        if (event.key === "Escape") setEditingDbaName(false);
                      }}
                    />
                  </fieldset>
                ) : (
                  <span>{dbaName}</span>
                )}
              </TableCell>
              <TableCell className={"information-actions"}>
                {editingDbaName ? (
                  savingDbaName ? (
                    <span style={{transform: "scale(0.5)", display: "inline-flex"}}>
                      <Spinner data-testid="dba-name-saving-spinner" />
                    </span>
                  ) : (
                    <ActionButtons>
                      <Tooltip position="top" toolTipContent="Save DBA Name" size="small">
                        <Button
                          data-testid="dba-name-save-button"
                          className="edit-button edit-button--inline"
                          icon={<CheckCircleIcon />}
                          size="default"
                          buttonStyle="secondary"
                          onClick={handleDbaNameSave}
                        />
                      </Tooltip>
                      <Tooltip position="top" toolTipContent="Cancel editing" size="small">
                        <Button
                          data-testid="dba-name-cancel-button"
                          className="edit-button edit-button--inline"
                          icon={<XMarkIcon />}
                          size="default"
                          buttonStyle="secondary"
                          onClick={() => setEditingDbaName(false)}
                        />
                      </Tooltip>
                    </ActionButtons>
                  )
                ) : (
                  <ActionButtons>
                    <EditButton
                      onClick={startEditingDbaName}
                      qualifier={"dba-name"}
                      tooltip={"Edit DBA Name"}
                      inline={true}
                    />
                  </ActionButtons>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={"information-field"}>Common Org ID</TableCell>
              <TableCell className={"information-value"}>{id}</TableCell>
              <TableCell className={"information-actions"}>
                <ActionButtons>
                  <CopyButton
                    content={id}
                    qualifier={"common-org-id"}
                    item={"Common Org ID"}
                    inline={true}
                  />
                </ActionButtons>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={"information-field"}>Parent ID</TableCell>
              <TableCell
                className={"information-value"}
                id={"dealer-information-parent-id"}
                data-testid={"dealer-information-parent-id"}
              >
                {parent ? (
                  <Popover
                    id={"entity-parent-popover"}
                    data-testid={"entity-parent-popover"}
                    className={"entity-parent-popover"}
                    trigger={"outsideClick"}
                    position={"bottom"}
                    popoverContent={
                      <EntityCard
                        id={"entity-card-parent"}
                        className={"popover-entity-card"}
                        heading={"h4"}
                        entity={parent}
                        footer={
                          <Button
                            id={`entity-card-button-${parent.id}`}
                            className={"entity-card-button-parent"}
                            buttonStyle={"tertiary"}
                            onClick={e => {
                              e.preventDefault();
                              if (parent) {
                                navigateToEntityFromEvent(e, parent.id);
                              }
                            }}
                          >
                            View Parent
                          </Button>
                        }
                      />
                    }
                  >
                    <span
                      className={"entity-parent"}
                      onClick={() => {
                        productEvent({
                          name: "parent_clicked",
                          properties: {
                            location: "Dealer Details Overview",
                            value: `${parent?.id}`,
                            result: "Parent Displayed"
                          }
                        });
                      }}
                    >
                      {parent.id}
                    </span>
                  </Popover>
                ) : (
                  "No Parent"
                )}
              </TableCell>
              <TableCell className={"information-actions"}>
                <ActionButtons>
                  {parent?.id && (
                    <CopyButton
                      content={parent.id}
                      qualifier={"parent-id"}
                      item={"Parent ID"}
                      inline={true}
                    />
                  )}
                  <PermissionBasedEditButton
                    onClick={() => setEditingParent(true)}
                    qualifier={"parent-id"}
                    tooltip={"Edit Parent ID"}
                    inline={true}
                    permissions={[EDIT_PARENT_ID]}
                  />
                </ActionButtons>
              </TableCell>
            </TableRow>
            {showTenancyTab ? (
              <TableRow>
                <TableCell className={"information-field"}>Tenant Name</TableCell>
                <TableCell
                  className={"information-value"}
                  id={"client-hierarchy-entity-information-tenant-name"}
                  data-testid={"client-hierarchy-entity-information-tenant-name"}
                >
                  {tenantId ? (
                    <a
                      href={"#tenancy"}
                      className={"tenant-name-link"}
                      data-testid={"overview-tenant-name-link"}
                      onClick={activateTenancyTab}
                    >
                      {tenant?.name ?? tenantId}
                    </a>
                  ) : (
                    "No Tenant"
                  )}
                </TableCell>
                <TableCell className={"information-actions"} />
              </TableRow>
            ) : (
              <TableRow>
                <TableCell className={"information-field"}>Tenant ID</TableCell>
                <TableCell className={"information-value"}>{tenantId ?? "No Tenant"}</TableCell>
                <TableCell className={"information-actions"}>
                  {tenantId && (
                    <ActionButtons>
                      <CopyButton
                        content={tenantId}
                        qualifier={"tenant-id"}
                        item={"Tenant ID"}
                        inline={true}
                      />
                    </ActionButtons>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </SimpleTable>
      </TabRow>
      <Sources id={id} sources={sources} isComplex={isComplex} />
      {!hasCacmSource ? (
        <TabRow
          name={"operational-address"}
          header={
            <h4 style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              Address
              {editingAddress ? (
                savingAddress ? (
                  <span style={{transform: "scale(0.5)", display: "inline-flex"}}>
                    <Spinner data-testid="address-saving-spinner" />
                  </span>
                ) : (
                  <span style={{display: "flex", gap: "4px"}}>
                    <Tooltip position="top" toolTipContent="Save address" size="small">
                      <Button
                        data-testid="address-save-button"
                        className="edit-button edit-button--inline"
                        icon={<CheckCircleIcon />}
                        size="default"
                        buttonStyle="secondary"
                        onClick={handleAddressSave}
                      />
                    </Tooltip>
                    <Tooltip position="top" toolTipContent="Cancel editing" size="small">
                      <Button
                        data-testid="address-cancel-button"
                        className="edit-button edit-button--inline"
                        icon={<XMarkIcon />}
                        size="default"
                        buttonStyle="secondary"
                        onClick={() => setEditingAddress(false)}
                      />
                    </Tooltip>
                  </span>
                )
              ) : (
                <EditButton
                  onClick={startEditingAddress}
                  qualifier="address"
                  tooltip="Edit Address"
                  inline={false}
                />
              )}
            </h4>
          }
        >
          {editingAddress ? (
            <fieldset disabled={savingAddress} style={{border: "none", padding: 0, margin: 0, opacity: savingAddress ? 0.5 : 1}}>
              <SimpleTable
                data-testid="inline-address-form"
                dataDensity={"small"}
                background={"white"}
                className={"operational-address-table"}
                bordered={false}
                hover={false}
                striped={false}
              >
                <TableBody className={"address-table-body address-body"}>
                  <TableRow className={"address-row"}>
                    <TableCell className={"address-field"}>Street</TableCell>
                    <TableCell className={"address-value"}>
                      <TextInput
                        id={"address-street1"}
                        data-testid={"address-street1"}
                        name={"address-street1"}
                        label={"Street"}
                        displayLabel={false}
                        size={"small"}
                        value={addressForm.streetAddressLine1}
                        onChange={e => updateAddressField("streetAddressLine1", e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className={"address-row"}>
                    <TableCell className={"address-field"}>Suite</TableCell>
                    <TableCell className={"address-value"}>
                      <TextInput
                        id={"address-street2"}
                        data-testid={"address-street2"}
                        name={"address-street2"}
                        label={"Suite"}
                        displayLabel={false}
                        size={"small"}
                        value={addressForm.streetAddressLine2}
                        onChange={e => updateAddressField("streetAddressLine2", e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className={"address-row"}>
                    <TableCell className={"address-field"}>City</TableCell>
                    <TableCell className={"address-value"}>
                      <TextInput
                        id={"address-city"}
                        data-testid={"address-city"}
                        name={"address-city"}
                        label={"City"}
                        displayLabel={false}
                        size={"small"}
                        value={addressForm.city}
                        onChange={e => updateAddressField("city", e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className={"address-row"}>
                    <TableCell className={"address-field"}>State</TableCell>
                    <TableCell className={"address-value"}>
                      <TextInput
                        id={"address-state"}
                        data-testid={"address-state"}
                        name={"address-state"}
                        label={"State"}
                        displayLabel={false}
                        size={"small"}
                        value={addressForm.state}
                        onChange={e => updateAddressField("state", e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className={"address-row"}>
                    <TableCell className={"address-field"}>Zip</TableCell>
                    <TableCell className={"address-value"}>
                      <TextInput
                        id={"address-zip"}
                        data-testid={"address-zip"}
                        name={"address-zip"}
                        label={"Zip"}
                        displayLabel={false}
                        size={"small"}
                        value={addressForm.zip}
                        onChange={e => updateAddressField("zip", e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className={"address-row"}>
                    <TableCell className={"address-field"}>Country</TableCell>
                    <TableCell className={"address-value"}>
                      <TextInput
                        id={"address-country"}
                        data-testid={"address-country"}
                        name={"address-country"}
                        label={"Country"}
                        displayLabel={false}
                        size={"small"}
                        value={addressForm.country}
                        onChange={e => updateAddressField("country", e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </SimpleTable>
            </fieldset>
          ) : (
            <SimpleTable
              id={`operational-address-table-${id}`}
              data-testid={`operational-address-table-${id}`}
              dataDensity={"small"}
              background={"white"}
              className={"operational-address-table"}
              bordered={false}
              hover={false}
              striped={false}
            >
              <AddressTableBody
                id={id}
                streetAddressLine1={address?.streetAddressLine1}
                streetAddressLine2={address?.streetAddressLine2}
                city={address?.city}
                state={address?.state}
                country={address?.country}
                zip={address?.zip}
              />
            </SimpleTable>
          )}
        </TabRow>
      ) : (
        <OperationalAddress
          id={id}
          streetAddressLine1={address?.streetAddressLine1}
          streetAddressLine2={address?.streetAddressLine2}
          city={address?.city}
          state={address?.state}
          country={address?.country}
          zip={address?.zip}
          sources={sources}
        />
      )}
      {!hasCacmSource ? (
        <TabRow
          name={"operational-telecom-numbers"}
          header={
            <h4 style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              Phone
              {editingTelecom ? (
                savingTelecom ? (
                  <span style={{transform: "scale(0.5)", display: "inline-flex"}}>
                    <Spinner data-testid="telecom-saving-spinner" />
                  </span>
                ) : (
                  <span style={{display: "flex", gap: "4px"}}>
                    <Tooltip position="top" toolTipContent="Save phone numbers" size="small">
                      <Button
                        data-testid="telecom-save-button"
                        className="edit-button edit-button--inline"
                        icon={<CheckCircleIcon />}
                        size="default"
                        buttonStyle="secondary"
                        onClick={handleTelecomSave}
                      />
                    </Tooltip>
                    <Tooltip position="top" toolTipContent="Cancel editing" size="small">
                      <Button
                        data-testid="telecom-cancel-button"
                        className="edit-button edit-button--inline"
                        icon={<XMarkIcon />}
                        size="default"
                        buttonStyle="secondary"
                        onClick={() => setEditingTelecom(false)}
                      />
                    </Tooltip>
                  </span>
                )
              ) : (
                <EditButton
                  onClick={startEditingTelecom}
                  qualifier="telecom-numbers"
                  tooltip="Edit Phone Numbers"
                  inline={false}
                />
              )}
            </h4>
          }
        >
          <SimpleTable
            id={`operational-telecom-numbers-table-${id}`}
            data-testid={`operational-telecom-numbers-table-${id}`}
            dataDensity={"small"}
            background={"white"}
            className={"operational-telecom-numbers-table"}
            bordered={false}
            hover={false}
            striped={false}
          >
            <TableBody
              id={`telecom-numbers-table-body-${id}`}
              className={"telecom-numbers-table-body telecom-numbers-body"}
            >
              <TableRow className={"telecom-numbers-row"}>
                <TableCell className={"telecom-numbers-field"}>Phone</TableCell>
                <TableCell className={"telecom-numbers-value"} id={"telecom-numbers-phone-numbers"}>
                  {editingTelecom ? (
                    <fieldset disabled={savingTelecom} style={{border: "none", padding: 0, margin: 0, opacity: savingTelecom ? 0.5 : 1}}>
                      <TextInput
                        id={"inline-input-telecom"}
                        data-testid={"inline-input-telecom"}
                        name={"inline-input-telecom"}
                        label={"Phone numbers"}
                        displayLabel={false}
                        size={"small"}
                        value={telecomValue}
                        onChange={e => setTelecomValue(e.target.value)}
                        placeholder={"Phone numbers (comma-separated)"}
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === "Enter") handleTelecomSave();
                          if (e.key === "Escape") setEditingTelecom(false);
                        }}
                      />
                    </fieldset>
                  ) : (
                    <ul className={"phone-numbers"}>
                      {telecomNumbers?.map((phone: string) => (
                        <li key={phone} className={"phone-number"}>{phone}</li>
                      ))}
                    </ul>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </SimpleTable>
        </TabRow>
      ) : (
        <OperationalTelecomNumbers
          id={id}
          sources={sources}
          currentNumbers={telecomNumbers}
          displayEditButton={true}
        />
      )}
      {!hasCacmSource ? (
        <SyntheticOperationalOems id={id} oems={oems} />
      ) : (
        <OperationalOems id={id} oems={oems} sources={sources} />
      )}
      <EnterpriseEntitlements />
    </TabColumn>
  );
};
